import { type PoseBoneSnapshot, type PoseSnapshot } from './PoseSnapshot'

/**
 * Pure data ops on `PoseSnapshot`s that aren't capture-from-skeleton (which lives
 * in `PoseSnapshotUtil`) and aren't write-to-clip (which lives in
 * `KeyframeBuilder`). These are the in-between transformations: retiming a
 * snapshot, mirroring it left↔right.
 *
 * Kept separate from `PoseSnapshotUtil` so the snapshot layer doesn't acquire
 * mirror-axis assumptions from the humanoid rig. The mirroring math here matches
 * `AnimationUtility.apply_animation_mirroring` (used for whole-clip mirror via
 * the toolbar checkbox) — same convention applied at single-snapshot granularity.
 */
export class PoseOps {
  /** Returns a new snapshot with the given time. The bones array is reused (callers don't typically mutate it). */
  static at_time (snapshot: PoseSnapshot, time: number): PoseSnapshot {
    return { time, bones: snapshot.bones }
  }

  /**
   * Returns a new snapshot with left/right bone snapshots swapped and quaternions
   * mirrored. Bones whose name ends in `_l` (case-insensitive) become `_r`, and
   * vice versa, with the mirrored quaternion. Center bones (spine/hips/pelvis/
   * neck/head/torso/chest) get their quaternion mirrored in place. Position
   * components on center bones get their X coordinate flipped.
   *
   * Bones that aren't paired and aren't center bones are passed through unchanged.
   */
  static mirror (snapshot: PoseSnapshot): PoseSnapshot {
    const out: PoseBoneSnapshot[] = []

    // Build a quick lookup by lowercased name so we can find a left bone's
    // mirror partner without a linear search per bone.
    const by_lower = new Map<string, PoseBoneSnapshot>()
    for (const b of snapshot.bones) by_lower.set(b.bone_name.toLowerCase(), b)

    for (const bone of snapshot.bones) {
      const lower = bone.bone_name.toLowerCase()

      if (lower.endsWith('_l')) {
        const partner_name = bone.bone_name.slice(0, -2) + '_r' // preserve original casing on the rest
        const partner_lower = lower.slice(0, -2) + '_r'
        const partner = by_lower.get(partner_lower)
        if (partner !== undefined) {
          // Swap: this slot gets the partner's data with bone_name retained for THIS bone.
          out.push({
            bone_name: bone.bone_name,
            quaternion: this.mirror_quat(partner.quaternion),
            position: partner.position !== undefined ? [-partner.position[0], partner.position[1], partner.position[2]] : undefined
          })
          out.push({
            bone_name: partner_name,
            quaternion: this.mirror_quat(bone.quaternion),
            position: bone.position !== undefined ? [-bone.position[0], bone.position[1], bone.position[2]] : undefined
          })
        } else {
          // No partner — pass through with mirrored quaternion (still useful).
          out.push({ bone_name: bone.bone_name, quaternion: this.mirror_quat(bone.quaternion), position: bone.position })
        }
      } else if (lower.endsWith('_r')) {
        // Already handled when its `_l` partner was processed (if present).
        const partner_lower = lower.slice(0, -2) + '_l'
        if (by_lower.has(partner_lower)) continue
        // No `_l` partner — pass through with mirrored quaternion.
        out.push({ bone_name: bone.bone_name, quaternion: this.mirror_quat(bone.quaternion), position: bone.position })
      } else if (this.is_center_bone(lower)) {
        out.push({
          bone_name: bone.bone_name,
          quaternion: this.mirror_quat(bone.quaternion),
          position: bone.position !== undefined ? [-bone.position[0], bone.position[1], bone.position[2]] : undefined
        })
      } else {
        out.push(bone)
      }
    }

    return { time: snapshot.time, bones: out }
  }

  // -- Internals ------------------------------------------------------------

  private static mirror_quat (q: [number, number, number, number]): [number, number, number, number] {
    // Same convention as AnimationUtility.mirror_quaternion_track_values: invert
    // X and W. Mathematically this is the reflection of a humanoid-rig rotation
    // across the YZ plane (X axis flipped).
    return [-q[0], q[1], q[2], -q[3]]
  }

  private static is_center_bone (lower_name: string): boolean {
    return (
      lower_name.includes('spine') ||
      lower_name.includes('hips') ||
      lower_name.includes('pelvis') ||
      lower_name.includes('neck') ||
      lower_name.includes('head') ||
      lower_name.includes('torso') ||
      lower_name.includes('chest')
    )
  }
}
